/**
 * Local dev "logged-in" mode.
 *
 * There is no backend in this repo, so on localhost `checkAuth` (GET /users/me)
 * always fails and the app renders as a guest — you can never exercise the
 * logged-in UI (save / share panel, the user's own saved-work list, logged-in
 * card decks) without a real server.
 *
 * Activate this mode by adding `?local-login` to the URL on a local host, e.g.
 *   http://localhost:8080/?local-login
 * It forces a logged-in session with a dummy user and seeds the saved-work list
 * with a handful of dummy share codes so the logged-in experience is browsable
 * offline. It is a no-op on any non-local host, so it can never leak to a real
 * deployment.
 *
 * Scope: this only fakes auth *state*, the share-code *list*, and *ownership* of
 * the codes in that list (see isDummyOwnedCode). Actions that round-trip to the
 * backend (generating a real code, saving work) still hit the missing API and
 * will fail — the mode is for viewing the logged-in UI, not for end-to-end
 * save/share.
 */
import { formatShareCode } from './shareCode';
/** URL param that turns the mode on (also the reserved key the share-code
 *  parser must skip so `?local-login` isn't read as a share code). */
export var LOCAL_LOGIN_PARAM = 'local-login';
/** True when the app is running on a local / private-network host. Mirrors the
 *  `isLocalhost` check in PlayerMainContainer so the mode can never activate on
 *  a public deployment. */
function isLocalHost() {
    var host = window.location.hostname;
    return (host === 'localhost' ||
        host === '127.0.0.1' ||
        host === '[::1]' ||
        host === 'bs-local.com' ||
        /^192\.168\.\d+\.\d+$/.test(host) ||
        /^10\.\d+\.\d+\.\d+$/.test(host) ||
        /^172\.(1[6-9]|2\d|3[01])\.\d+\.\d+$/.test(host));
}
/** Whether the dummy logged-in mode is active for this page load. */
export function isLocalLoginMode() {
    try {
        return isLocalHost() && new URLSearchParams(window.location.search).has(LOCAL_LOGIN_PARAM);
    }
    catch (_a) {
        return false;
    }
}
/** Dummy identity used while the mode is active. */
export var DUMMY_USER = {
    username: 'local.dev',
    userId: 999999,
};
/** A few dummy share-code entries grouped into sections, matching the shape the
 *  saved-work panel (`fetchGroups`) normally receives from `GET /share-codes`.
 *  Codes are stable so reloads look consistent.
 *
 *  Shaped so the panel's states are all reachable offline:
 *
 *  - **Uncategorized is empty and nothing is favourited**, so both always-present
 *    sections show their empty note ("No Uncategorized workspaces" / "No saved
 *    Favorites"). Those notes are otherwise untestable here — every way to empty
 *    a folder through the UI is a server call, and there is no backend locally.
 *  - The demo folder's labels are deliberately **not** in alphabetical order and
 *    its `updatedAt` stamps are **not** in list order, so "Sort by Name" and
 *    "Sort by Most Recent" each visibly reorder it and produce *different*
 *    orders. With an already-sorted seed both are no-ops that look like a bug.
 *  - Five rows is enough to exercise the footer's 0 / 1 / 2-selected states.
 *  - One extra row carries `shareOnly: true`. It is the seed's only way to
 *    exercise the share-vs-save rule offline: `fetchGroups` filters it out, so
 *    a panel showing six rows means the filter regressed. See
 *    docs/features/ShareOnlyCodes.md.
 */
export function getDummyShareCodeSections() {
    var entry = function (code, label, updatedAt, isFavourite, shareOnly) {
        if (isFavourite === void 0) { isFavourite = false; }
        if (shareOnly === void 0) { shareOnly = false; }
        return ({
            id: "local-".concat(code),
            code: code,
            label: label,
            isFavourite: isFavourite,
            updatedAt: updatedAt,
            shareOnly: shareOnly,
        });
    };
    return [
        {
            id: 'ungrouped',
            titleKey: 'text.uncategorized',
            entries: [],
        },
        {
            id: 'local-grade-3',
            titleKey: 'Grade 3 (demo)',
            entries: [
                entry('36U2-432W', 'Spinner — demo', '2026-02-11T09:00:00Z'),
                entry('DEMO0001', 'Fraction Tiles — demo', '2026-01-04T09:00:00Z'),
                entry('DEMO0005', 'Coins — demo', '2026-03-27T09:00:00Z'),
                entry('DEMO0002', 'Array Model — demo', '2026-02-02T09:00:00Z'),
                entry('DEMO0004', 'Number Line — demo', '2026-01-19T09:00:00Z'),
                // Minted by Share, never saved — must NOT reach the panel.
                entry('DEMO0006', '', '2026-04-02T09:00:00Z', false, true),
            ],
        },
    ];
}
/**
 * Does the dummy user own this share code?
 *
 * Ownership normally comes from the backend: `checkAuth` fetches the share-code
 * record and compares `createdBy` / `meta.username` against the signed-in user.
 * There is no backend here, so this mode answers the same question from the only
 * ownership signal it has — the dummy saved-work list. A code in that list is
 * one of "your" workspaces; anything else is someone else's.
 *
 * This is what makes the logged-in NON-OWNER states reachable offline. Without
 * it the mode hardcoded `isAdmin = true`, so every code looked like your own and
 * the whole non-owner half of the UI was untestable locally: the view/edit
 * toggle's seed (`viewEditModeSeed`), the "This activity already has a share
 * code" save-edits reminder, and Save As.
 *
 * Compared through `formatShareCode` so the URL form and the seeded form match
 * regardless of hyphenation or case — `?36u2432w` and `36U2-432W` are the same
 * code, and PlayerMainContainer normalizes before it reaches the store.
 */
export function isDummyOwnedCode(code) {
    if (!code)
        return true; // no code loaded — your own blank workspace
    var target = formatShareCode(code);
    return getDummyShareCodeSections().some(function (section) { var _a; return ((_a = section.entries) !== null && _a !== void 0 ? _a : []).some(function (entry) { return formatShareCode(entry.code) === target; }); });
}
